import {
  ExploreSimilarSearchCards,
  buildExploreSimilarSearchCards,
} from '@/utils/buildExploreSimilarSearchLocationCards';
import {
  buildCanonicalJobListingUrl,
  createKnownSpecialitySlugs,
  derivePageHeaderContext,
  extractPerfectlyMatchingResults,
  extractSimilarJobs,
  filterCanonicalSpecialitySummaries,
  getSearchedGrade,
  parseQueryForGetJobs,
  toSingleQueryValue,
} from '@/utils/jobListingHelper';
import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import { GetServerSidePropsResult } from 'next/types';
import React from 'react';
import { getJobSpecialities } from 'services/cmsService';
import { getJobs } from 'services/jobService';
import { IArticleSummary, IFrequentlyAskedQuestion } from 'types/cms';
import {
  IJobDetail,
  IJobSpeciality,
  JobListPaginatedResults,
  JobSearchWebCriteria,
} from 'types/jobs';
import { getDoctorTestimonials } from 'utils/getDoctorTestimonials';
import { getDoctorsFaqs } from 'utils/getDoctorsFaqs';
import { getGuidesForContext } from 'utils/getGuidesForContext';
import { getJobsForExploreSimilarSearches } from 'utils/getJobsForExploreSimilarSearches';
import { getRequestOrigin } from 'utils/getRequestOrigin';

import JobListing from 'modules/JobListing';
import { filterFaqsForContext } from 'modules/JobListing/utils/filterFaqsForContext';
import {
  TestimonialsForContext,
  filterTestimonialsForContext,
} from 'modules/JobListing/utils/filterTestimonialsForContext';
import { ContainerStyle, UserLoggedinContainer } from 'modules/UserLoggedinContainer';

import styles from './style.module.scss';

interface JobsListProps {
  jobs: JobListPaginatedResults;
  similarJobs: IJobDetail[];
  jobSpecialities: IJobSpeciality[];
  query: JobSearchWebCriteria;
  guides: IArticleSummary[];
  // Already filtered/ranked against the (empty, on this generic route) search context - see
  // derivePageHeaderContext - so the full CMS collection isn't serialized into the page.
  faqs: IFrequentlyAskedQuestion[];
  testimonialsForContext: TestimonialsForContext;
  exploreSimilarSearchCards: ExploreSimilarSearchCards;
  origin: string;
  canonicalUrl: string;
}

const JobsList = ({
  jobs,
  query,
  jobSpecialities,
  similarJobs,
  guides,
  faqs,
  testimonialsForContext,
  exploreSimilarSearchCards,
  origin,
  canonicalUrl,
}: JobsListProps) => {
  const title = 'Search all doctor jobs in Australia & NZ';
  const description = `Find your next doctor job with Medrecruit. ${jobs.total} jobs available across Australia and NZ. Find better experiences & better pay. Free end to end service for doctors.`;

  return (
    <UserLoggedinContainer
      size={false}
      className={styles.jobListContainer}
      style={ContainerStyle.NO_BANNER}
      fullWidth={true}
      noFooterMargin
    >
      <NextSeo
        canonical={canonicalUrl}
        title={title}
        description={description}
        openGraph={{
          title,
          description,
        }}
      />
      <JobListing
        jobs={jobs}
        query={query}
        jobSpecialities={jobSpecialities}
        similarJobs={similarJobs}
        guides={guides}
        faqs={faqs}
        testimonialsForContext={testimonialsForContext}
        exploreSimilarSearchCards={exploreSimilarSearchCards}
        origin={origin}
      />
    </UserLoggedinContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<JobsListProps>> {
  const { query } = ctx;

  const convertedQuery = parseQueryForGetJobs(query);

  // Computed up front (it's a pure function of query, no CMS/API dependency) so getGuidesForContext
  // can join the same Promise.all below instead of running as an extra sequential round trip.
  const pageHeaderContext = derivePageHeaderContext(query);

  const [jobs, jobSpecialitiesResult, guides, faqs, testimonials, exploreSimilarSearchesJobs] =
    await Promise.all([
      getJobs(convertedQuery, ctx) as Promise<JobListPaginatedResults>,
      getJobSpecialities(),
      getGuidesForContext(pageHeaderContext),
      getDoctorsFaqs(),
      getDoctorTestimonials(),
      getJobsForExploreSimilarSearches(ctx),
    ]);

  const jobSpecialities = jobSpecialitiesResult ?? [];
  const knownSpecialitySlugs = createKnownSpecialitySlugs(jobSpecialities);
  const origin = getRequestOrigin(ctx);

  // See the equivalent comment in pages/jobs/listing/[[...listing]].tsx - rank/cap testimonials and
  // FAQs against the search context here so the full CMS collection isn't serialized into the
  // page just to be filtered down client-side. Guides are already ranked by getGuidesForContext.
  const testimonialsForContext = filterTestimonialsForContext(testimonials, pageHeaderContext);
  const contextFaqs = filterFaqsForContext(faqs, pageHeaderContext);

  // This page is no longer reachable through the app's own search flow (every filter now routes
  // to /jobs/listing - see generateJobListingsUrlObject), but it still receives real traffic from
  // the shared JobListing "Sort by" control (which always pushes to PATH.JOBSLIST) and from old
  // bookmarked/external links carrying grade/speciality/location/workType query params. Point the
  // canonical at the equivalent /jobs/listing URL - the same one those filters would produce if
  // submitted through Search - rather than self-referencing this page, so search engines
  // consolidate onto the one real canonical location instead of indexing this page too.
  const canonicalUrl = buildCanonicalJobListingUrl(
    {
      grade: pageHeaderContext.grade,
      speciality: pageHeaderContext.speciality,
      location: pageHeaderContext.location,
      workType: toSingleQueryValue(query.workType),
    },
    origin,
  );

  const exploreSimilarSearchJobsPool = filterCanonicalSpecialitySummaries(
    exploreSimilarSearchesJobs,
    knownSpecialitySlugs,
  );

  return {
    props: {
      jobs: extractPerfectlyMatchingResults(jobs),
      similarJobs: extractSimilarJobs(jobs.data, getSearchedGrade(convertedQuery.grade)),
      query,
      jobSpecialities,
      guides,
      faqs: contextFaqs,
      testimonialsForContext,
      exploreSimilarSearchCards: buildExploreSimilarSearchCards(
        exploreSimilarSearchJobsPool,
        query,
        origin,
      ),
      origin,
      canonicalUrl,
    },
  };
}

export default JobsList;
